// ─────────────────────────────────────────────────────────
// Einstellungen → Mitarbeiter (plancraft-Stil)
// Eigenes Modul, damit CompanyDashboard.js nicht weiter wächst.
// Deutsche UI. Nutzt bestehende APIs (members/roles/invitations).
// Hinweis: Einladung im Backend = nur E-Mail + Rolle. Weitere
// plancraft-Felder (Vorname/Arbeitszeitmodell/Mobil/Gültig-ab)
// brauchen erst Backend-Erweiterung + Migration.
// Sprache: alle sichtbaren Texte über t() — Keys in i18n/de.json + en.json.
// ─────────────────────────────────────────────────────────
import { icon, escapeHtml } from '../../utils/dom.js';
import { getUser } from '../../core/auth.js';
import { createInvitation, revokeInvitation, setMemberRole, updateOwnProfile, updateMemberProfile, getOwnProfileFull, getMemberProfileFull } from '../../core/org.js';
import { t } from '../../i18n/index.js';

const DASH = '–';

function roleName(role) { return role && role.name ? role.name : DASH; }
function dateInput(v) { return v ? String(v).slice(0, 10) : ''; } // ISO → YYYY-MM-DD fürs <input type=date>

// ── eine Tabellenzeile (Profil / Mitarbeiter) ──
// Klickbar zum Bearbeiten, wenn: eigener Datensatz ODER members:write (Admin).
function personRow(m, ctx, isSelf) {
  const { grad, initials, can } = ctx;
  const editable = isSelf || can('members:write');
  const clickAttr = editable ? ` data-action="emp-edit-open" data-id="${escapeHtml(m.id)}"` : '';
  return `
    <div class="cd-emp-trow${editable ? ' cd-emp-trow-click' : ''}"${clickAttr}>
      <div class="cd-emp-name">
        <span class="cd-ava" style="background:${grad(m.name || m.email)}">${escapeHtml(initials(m.name, m.email))}</span>
        <span>${escapeHtml(m.name || m.email)}${isSelf ? ` <span class="cd-you">(${escapeHtml(t('settings.employees.you'))})</span>` : ''}</span>
      </div>
      <div>${escapeHtml(roleName(m.role))}</div>
      <div class="cd-emp-mail">${escapeHtml(m.email || DASH)}</div>
      <div>${DASH}</div>
      <div>${DASH}</div>
    </div>`;
}

function tableHead() {
  return `
  <div class="cd-emp-thead">
    <span>${escapeHtml(t('common.name'))}</span><span>${escapeHtml(t('settings.employees.role'))}</span><span>${escapeHtml(t('settings.employees.email'))}</span><span>${escapeHtml(t('settings.employees.wageGroup'))}</span><span>${escapeHtml(t('settings.employees.workingTimeModel'))}</span>
  </div>`;
}

function invitationRow(inv, ctx) {
  const { can } = ctx;
  const menu = can('members:invite')
    ? `<button class="cd-emp-rowbtn" data-action="emp-inv-revoke" data-id="${escapeHtml(inv.id)}" title="${escapeHtml(t('settings.employees.revokeInvitation'))}">${icon('more_vert', 18)}</button>`
    : '<span></span>';
  return `
    <div class="cd-emp-trow cd-emp-trow-inv">
      <div class="cd-emp-mail">${escapeHtml(inv.email)}</div>
      <div>${escapeHtml(roleName(inv.role))}</div>
      ${menu}
    </div>`;
}

// ── Hauptinhalt des Mitarbeiter-Bereichs (ohne Testversion-Banner) ──
export function renderEmployeesSection(ctx) {
  const { s } = ctx;
  const me = getUser() || {};

  if (s.loading) return `<div class="cd-set-empty">${escapeHtml(t('common.loading'))}</div>`;

  const info = `
    <div class="cd-emp-info">${icon('info', 18)}<span>${escapeHtml(t('settings.employees.freeLimitInfo'))}</span></div>`;

  const profile = `
    <div class="cd-emp-card">
      <div class="cd-emp-card-head">${icon('person', 18)}<h3>${escapeHtml(t('settings.employees.yourProfile'))}</h3></div>
      <div class="cd-emp-table">${tableHead()}${personRow(me, ctx, true)}</div>
    </div>`;

  const others = (s.members || []).filter((m) => m.id !== me.id);
  const membersBody = others.length
    ? `<div class="cd-emp-table">${tableHead()}${others.map((m) => personRow(m, ctx, false)).join('')}</div>`
    : `<div class="cd-emp-empty">${escapeHtml(t('settings.employees.noMembers'))}</div>`;
  const members = `
    <div class="cd-emp-card">
      <div class="cd-emp-card-head">${icon('groups', 18)}<h3>${escapeHtml(t('settings.employees.yourMembers'))}</h3></div>
      ${membersBody}
    </div>`;

  const invs = s.invitations || [];
  const invBody = invs.length
    ? `<div class="cd-emp-table">
         <div class="cd-emp-thead cd-emp-thead-inv"><span>${escapeHtml(t('settings.employees.email'))}</span><span>${escapeHtml(t('settings.employees.role'))}</span><span></span></div>
         ${invs.map((inv) => invitationRow(inv, ctx)).join('')}
       </div>`
    : `<div class="cd-emp-empty">${escapeHtml(t('settings.employees.noOpenInvitations'))}</div>`;
  const invitations = `
    <div class="cd-emp-card">
      <div class="cd-emp-card-head">${icon('mail', 18)}<h3>${escapeHtml(t('settings.employees.openInvitations'))}</h3></div>
      ${invBody}
    </div>`;

  return `<div class="cd-emp">${info}${profile}${members}${invitations}</div>`;
}

// ── Einladen-Button für den Seitenkopf ──
export function inviteHeadButton(ctx) {
  if (!ctx.can('members:invite')) return '';
  return `<button class="cd-btn-primary" data-action="emp-invite-open">${icon('person_add', 16)}${escapeHtml(t('settings.employees.inviteMember'))}</button>`;
}

// ── Einladen-Modal ──
export function renderInviteModal(ctx) {
  const { s } = ctx;
  const inv = s.empInvite;
  if (!inv || !inv.open) return '';
  const roles = s.roles || [];
  const dis = inv.saving ? 'disabled' : '';
  const roleOptions = [`<option value="">${escapeHtml(t('settings.employees.selectRole'))}</option>`]
    .concat(roles.map((r) => `<option value="${escapeHtml(r.id)}" ${inv.roleId === r.id ? 'selected' : ''}>${escapeHtml(r.name)}</option>`))
    .join('');
  return `
    <div class="cd-emp-modal-overlay" data-action="emp-invite-close">
      <div class="cd-emp-modal cd-emp-modal-lg" data-action="emp-noop">
        <div class="cd-emp-modal-head">
          <h2>${icon('person_add', 20)}${escapeHtml(t('settings.employees.inviteMember'))}</h2>
          <button class="cd-emp-modal-x" data-action="emp-invite-close" title="${escapeHtml(t('common.close'))}">${icon('close', 20)}</button>
        </div>
        <div class="cd-emp-modal-body">
          ${inv.error ? `<div class="cd-error">${icon('error', 18)}<span>${escapeHtml(inv.error)}</span></div>` : ''}
          <div class="cd-form-grid2">
            <label class="cd-field"><span>${escapeHtml(t('settings.employees.firstName'))}</span><input class="cd-input" id="emp-inv-firstName" type="text" value="${escapeHtml(inv.firstName || '')}" ${dis}></label>
            <label class="cd-field"><span>${escapeHtml(t('settings.employees.lastName'))}</span><input class="cd-input" id="emp-inv-lastName" type="text" value="${escapeHtml(inv.lastName || '')}" ${dis}></label>
          </div>
          <label class="cd-field">
            <span>${escapeHtml(t('settings.employees.emailAddressRequired'))}</span>
            <input class="cd-input" id="emp-inv-email" type="email" placeholder="${escapeHtml(t('settings.employees.emailPlaceholder'))}" value="${escapeHtml(inv.email || '')}" ${dis}>
          </label>
          <div class="cd-form-grid2">
            <label class="cd-field"><span>${escapeHtml(t('settings.employees.mobile'))}</span><input class="cd-input" id="emp-inv-mobile" type="tel" placeholder="${escapeHtml(t('settings.employees.mobilePlaceholder'))}" value="${escapeHtml(inv.mobile || '')}" ${dis}></label>
            <label class="cd-field"><span>${escapeHtml(t('settings.employees.startDate'))}</span><input class="cd-input" id="emp-inv-startDate" type="date" value="${escapeHtml(inv.startDate || '')}" ${dis}></label>
          </div>
          <label class="cd-field">
            <span>${escapeHtml(t('settings.employees.roleRequired'))}</span>
            <div class="cd-select-shell">
              <select class="cd-input" id="emp-inv-role" ${dis}>${roleOptions}</select>
              ${icon('expand_more', 18)}
            </div>
          </label>
          <p class="cd-emp-modal-hint">${icon('info', 15)}<span>${escapeHtml(t('settings.employees.inviteHint'))}</span></p>
        </div>
        <div class="cd-emp-modal-foot">
          <button class="cd-btn-ghost" data-action="emp-invite-close" ${dis}>${escapeHtml(t('common.cancel'))}</button>
          <button class="cd-btn-primary" data-action="emp-invite-submit" ${dis}>${escapeHtml(inv.saving ? t('settings.employees.sending') : t('settings.employees.invite'))}</button>
        </div>
      </div>
    </div>`;
}

// ── Bearbeiten-Modal (Profil / Mitarbeiter) ──
// Eigenes Profil: nur Name. Admin (members:write): Name + Rolle (auch fremde).
export function renderEditModal(ctx) {
  const { s } = ctx;
  const e = s.empEdit;
  if (!e || !e.open) return '';
  const roles = s.roles || [];
  const dis = e.saving ? 'disabled' : '';
  const f = e.form;
  const adminBlock = e.canAll ? `
    <div class="cd-form-section">${escapeHtml(t('settings.employees.sectionEmployment'))}</div>
    <div class="cd-form-grid2">
      <label class="cd-field"><span>${escapeHtml(t('settings.employees.startDate'))}</span><input class="cd-input" id="emp-edit-startDate" type="date" value="${escapeHtml(f.startDate || '')}" ${dis}></label>
      <label class="cd-field"><span>${escapeHtml(t('settings.employees.personnelNumber'))}</span><input class="cd-input" id="emp-edit-personnelNumber" type="text" value="${escapeHtml(f.personnelNumber || '')}" ${dis}></label>
    </div>
    <div class="cd-form-grid2">
      <label class="cd-field">
        <span>${escapeHtml(t('settings.employees.vacationDays'))}</span>
        <input class="cd-input" id="emp-edit-vacationDays" type="number" min="0" max="365" step="0.5" value="${escapeHtml(f.vacationDaysPerYear ?? '')}" placeholder="${escapeHtml(t('settings.employees.vacationDaysPlaceholder'))}" ${dis}>
        <small class="cd-field-hint">${escapeHtml(t('settings.employees.vacationDaysHint'))}</small>
      </label>
      <span></span>
    </div>
    <label class="cd-field">
      <span>${escapeHtml(t('settings.employees.role'))}</span>
      <div class="cd-select-shell">
        <select class="cd-input" id="emp-edit-role" ${dis}>
          <option value="">${escapeHtml(t('settings.employees.noRole'))}</option>
          ${roles.map((r) => `<option value="${escapeHtml(r.id)}" ${e.roleId === r.id ? 'selected' : ''}>${escapeHtml(r.name)}</option>`).join('')}
        </select>
        ${icon('expand_more', 18)}
      </div>
    </label>` : `
    <label class="cd-field"><span>${escapeHtml(t('settings.employees.role'))}</span><input class="cd-input" value="${escapeHtml(e.roleLabel || DASH)}" disabled></label>`;
  return `
    <div class="cd-emp-modal-overlay" data-action="emp-edit-close">
      <div class="cd-emp-modal cd-emp-modal-lg" data-action="emp-noop">
        <div class="cd-emp-modal-head">
          <h2>${icon('badge', 20)}${escapeHtml(e.isSelf ? t('settings.employees.yourProfile') : t('settings.employees.editMember'))}</h2>
          <button class="cd-emp-modal-x" data-action="emp-edit-close" title="${escapeHtml(t('common.close'))}">${icon('close', 20)}</button>
        </div>
        <div class="cd-emp-modal-body">
          ${e.error ? `<div class="cd-error">${icon('error', 18)}<span>${escapeHtml(e.error)}</span></div>` : ''}
          <div class="cd-form-section">${escapeHtml(t('settings.employees.sectionPerson'))}</div>
          <div class="cd-form-grid2">
            <label class="cd-field"><span>${escapeHtml(t('settings.employees.firstName'))}</span><input class="cd-input" id="emp-edit-firstName" type="text" value="${escapeHtml(f.firstName || '')}" ${dis}></label>
            <label class="cd-field"><span>${escapeHtml(t('settings.employees.lastName'))}</span><input class="cd-input" id="emp-edit-lastName" type="text" value="${escapeHtml(f.lastName || '')}" ${dis}></label>
          </div>
          <div class="cd-form-grid2">
            <label class="cd-field"><span>${escapeHtml(t('settings.employees.mobile'))}</span><input class="cd-input" id="emp-edit-mobile" type="tel" value="${escapeHtml(f.mobile || '')}" ${dis}></label>
            <label class="cd-field"><span>${escapeHtml(t('settings.employees.birthDate'))}</span><input class="cd-input" id="emp-edit-birthDate" type="date" value="${escapeHtml(f.birthDate || '')}" ${dis}></label>
          </div>
          <label class="cd-field"><span>${escapeHtml(t('settings.employees.loginEmail'))}</span><input class="cd-input" value="${escapeHtml(e.email || DASH)}" disabled></label>

          <div class="cd-form-section">${escapeHtml(t('settings.employees.sectionAddress'))}</div>
          <label class="cd-field"><span>${escapeHtml(t('settings.employees.street'))}</span><input class="cd-input" id="emp-edit-street" type="text" value="${escapeHtml(f.street || '')}" ${dis}></label>
          <div class="cd-form-grid2">
            <label class="cd-field"><span>${escapeHtml(t('settings.employees.zip'))}</span><input class="cd-input" id="emp-edit-zip" type="text" value="${escapeHtml(f.zip || '')}" ${dis}></label>
            <label class="cd-field"><span>${escapeHtml(t('settings.employees.city'))}</span><input class="cd-input" id="emp-edit-city" type="text" value="${escapeHtml(f.city || '')}" ${dis}></label>
          </div>

          <div class="cd-form-section">${escapeHtml(t('settings.employees.sectionOther'))}</div>
          <div class="cd-form-grid2">
            <label class="cd-field"><span>${escapeHtml(t('settings.employees.ahvNumber'))}</span><input class="cd-input" id="emp-edit-ahvNumber" type="text" placeholder="${escapeHtml(t('settings.employees.ahvPlaceholder'))}" value="${escapeHtml(f.ahvNumber || '')}" ${dis}></label>
            <label class="cd-field"><span>${escapeHtml(t('settings.employees.ibanLabel'))}</span><input class="cd-input" id="emp-edit-iban" type="text" placeholder="${escapeHtml(t('settings.employees.ibanPlaceholder'))}" value="${escapeHtml(f.iban || '')}" ${dis}></label>
          </div>
          ${adminBlock}
        </div>
        <div class="cd-emp-modal-foot">
          <button class="cd-btn-ghost" data-action="emp-edit-close" ${dis}>${escapeHtml(t('common.cancel'))}</button>
          <button class="cd-btn-primary" data-action="emp-edit-submit" ${dis}>${escapeHtml(e.saving ? t('common.saving') : t('common.save'))}</button>
        </div>
      </div>
    </div>`;
}

// ── Aktions-Handler (gibt true zurück, wenn behandelt) ──
export function handleEmployeesAction(a, el, ctx) {
  const { s, draw, reload } = ctx;
  switch (a) {
    case 'emp-invite-open':
      s.empInvite = { open: true, email: '', firstName: '', lastName: '', mobile: '', startDate: '', roleId: '', saving: false, error: '' };
      draw();
      return true;
    case 'emp-noop':
      // Klick auf den Modal-Inhalt: nichts tun (verhindert Schließen durchs Overlay)
      return true;
    case 'emp-invite-close':
      s.empInvite = null;
      draw();
      return true;
    case 'emp-invite-submit': {
      const inv = s.empInvite;
      if (!inv) return true;
      const val = (id) => (document.getElementById(id)?.value || '').trim();
      inv.firstName = val('emp-inv-firstName');
      inv.lastName = val('emp-inv-lastName');
      inv.email = val('emp-inv-email');
      inv.mobile = val('emp-inv-mobile');
      inv.startDate = val('emp-inv-startDate');
      inv.roleId = document.getElementById('emp-inv-role')?.value || '';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inv.email)) { inv.error = t('settings.employees.invalidEmail'); draw(); return true; }
      if (!inv.roleId) { inv.error = t('settings.employees.selectRoleError'); draw(); return true; }
      inv.saving = true; inv.error = ''; draw();
      const payload = { email: inv.email, roleId: inv.roleId };
      if (inv.firstName) payload.firstName = inv.firstName;
      if (inv.lastName) payload.lastName = inv.lastName;
      if (inv.mobile) payload.mobile = inv.mobile;
      if (inv.startDate) payload.startDate = inv.startDate;
      createInvitation(payload)
        .then(() => { s.empInvite = null; if (reload) reload(); else draw(); })
        .catch((e) => { if (s.empInvite) { s.empInvite.saving = false; s.empInvite.error = e.message || t('settings.employees.inviteFailed'); } draw(); });
      return true;
    }
    case 'emp-inv-revoke': {
      const id = el.dataset.id;
      if (!id) return true;
      revokeInvitation(id).then(() => { if (reload) reload(); else draw(); }).catch(() => { if (reload) reload(); });
      return true;
    }
    case 'emp-edit-open': {
      const id = el.dataset.id;
      const me = getUser() || {};
      const isSelf = id === me.id;
      const canAll = ctx.can('members:write');
      if (!isSelf && !canAll) return true; // keine Berechtigung
      const m = (s.members || []).find((x) => x.id === id) || (isSelf ? me : null);
      if (!m) return true;
      // Grunddaten sofort (aus Liste), sensible Felder async nachladen (self/admin)
      const fillForm = (src) => ({
        firstName: src.firstName || '', lastName: src.lastName || '', mobile: src.mobile || '',
        street: src.street || '', zip: src.zip || '', city: src.city || '',
        birthDate: dateInput(src.birthDate), startDate: dateInput(src.startDate),
        personnelNumber: src.personnelNumber || '', ahvNumber: src.ahvNumber || '', iban: src.iban || '',
        // Ferienanspruch: null (nicht gepflegt) bleibt leer — 0 waere eine Aussage
        vacationDaysPerYear: src.vacationDaysPerYear == null ? '' : String(src.vacationDaysPerYear),
      });
      s.empEdit = {
        open: true, id, isSelf, canAll, loading: true,
        email: m.email || '',
        roleId: m.role ? m.role.id : '', roleLabel: m.role ? m.role.name : '',
        form: fillForm(m), saving: false, error: '',
      };
      draw();
      const fetchFull = isSelf && !canAll ? getOwnProfileFull() : getMemberProfileFull(id);
      fetchFull
        .then((r) => {
          const full = (r && r.member) || {};
          if (s.empEdit && s.empEdit.id === id) {
            s.empEdit.form = fillForm(full);
            s.empEdit.email = full.email || s.empEdit.email;
            s.empEdit.roleId = full.role ? full.role.id : s.empEdit.roleId;
            s.empEdit.loading = false;
            draw();
          }
        })
        .catch(() => { if (s.empEdit && s.empEdit.id === id) { s.empEdit.loading = false; draw(); } });
      return true;
    }
    case 'emp-edit-close':
      s.empEdit = null;
      draw();
      return true;
    case 'emp-edit-submit': {
      const e = s.empEdit;
      if (!e) return true;
      const val = (id) => (document.getElementById(id)?.value || '').trim();
      // gemeinsame Felder (self + admin)
      const data = {
        firstName: val('emp-edit-firstName'), lastName: val('emp-edit-lastName'), mobile: val('emp-edit-mobile'),
        street: val('emp-edit-street'), zip: val('emp-edit-zip'), city: val('emp-edit-city'),
        birthDate: val('emp-edit-birthDate') || null, ahvNumber: val('emp-edit-ahvNumber'), iban: val('emp-edit-iban'),
      };
      // ALLE Felder VOR dem ersten draw() lesen. Der Neuaufbau baut das Modal
      // aus `e.form` (dem Serverstand) neu auf — was danach aus dem DOM gelesen
      // wird, ist nicht mehr das, was der Nutzer getippt hat. Genau daran gingen
      // Eintrittsdatum und Personalnummer bisher lautlos verloren.
      let adminData = null;
      let roleId = '';
      if (e.canAll) {
        // Leeres Ferienfeld = «nicht gepflegt» → null, damit die Übersicht keinen
        // Rest von 0 Tagen behauptet.
        const urlaub = val('emp-edit-vacationDays');
        adminData = {
          startDate: val('emp-edit-startDate') || null,
          personnelNumber: val('emp-edit-personnelNumber'),
          vacationDaysPerYear: urlaub === '' ? null : Number(urlaub.replace(',', '.')),
        };
        roleId = document.getElementById('emp-edit-role')?.value || '';
      }
      if (!data.firstName && !data.lastName) { e.error = t('settings.employees.nameRequired'); draw(); return true; }
      e.saving = true; e.error = ''; draw();
      const tasks = [];
      if (e.canAll) {
        Object.assign(data, adminData);
        tasks.push(updateMemberProfile(e.id, data));
        if (roleId !== (e.roleId || '')) tasks.push(setMemberRole(e.id, roleId || null));
      } else {
        tasks.push(updateOwnProfile(data));
      }
      Promise.all(tasks)
        .then(() => { s.empEdit = null; if (reload) reload(); else draw(); })
        .catch((err) => { if (s.empEdit) { s.empEdit.saving = false; s.empEdit.error = err.message || t('common.saveFailed'); } draw(); });
      return true;
    }
    default:
      return false;
  }
}
